import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import {
  API_ERR_TOAST_MS,
  buildApiErrCopyText,
  buildApiErrMeta,
  type ApiErrUser,
} from "./apiErrorReport";

export type ApiErrToastPayload = {
  message: string;
  variant: "error";
  duration: number;
  copyText: string;
};

type ShowFn = (data: ApiErrToastPayload) => void;
type UserFn = () => ApiErrUser | null | undefined;

type Cfg = InternalAxiosRequestConfig & {
  __retry?: boolean;
  skipErrorNotification?: boolean;
};

let show: ShowFn | null = null;
let userFn: UserFn | null = null;
let eid: number | null = null;

const isCancel = (err: AxiosError): boolean => {
  if (axios.isCancel(err)) return true;
  return (err as AxiosError & { code?: string }).code === "ERR_CANCELED";
};

const shouldSkip = (err: AxiosError): boolean => {
  const cfg = err.config as Cfg | undefined;
  if (cfg?.skipErrorNotification) return true;
  if (isCancel(err)) return true;
  if (err.response?.status !== 401) return false;
  if (cfg?.__retry) return false;
  const url = (cfg?.url ?? "").split("?")[0];
  if (url.includes("/auth/basic") || url.includes("/auth/register")) return false;
  if (url.endsWith("/token/refresh")) return false;
  return Boolean(localStorage.getItem("refreshToken") || localStorage.getItem("token"));
};

const onErr = (err: AxiosError) => {
  if (!shouldSkip(err)) {
    emit(err);
  }
  return Promise.reject(err);
};

const emit = (err: AxiosError) => {
  if (!show) return;
  const meta = buildApiErrMeta(err, userFn?.() ?? null);
  show({
    message: "Возникла ошибка",
    variant: "error",
    duration: API_ERR_TOAST_MS,
    copyText: buildApiErrCopyText(meta),
  });
};

export const forceApiErrToast = (err: unknown) => {
  if (!axios.isAxiosError(err)) return;
  emit(err);
};

export const bindApiErrToast = (fn: ShowFn, getUser: UserFn) => {
  show = fn;
  userFn = getUser;
};

export const installApiErrInterceptor = () => {
  if (eid != null) {
    axios.interceptors.response.eject(eid);
    eid = null;
  }
  eid = axios.interceptors.response.use((r) => r, onErr);
};
